<template>
	<div class="tour-schedule-drag-drop">
		<div class="tour-schedule-drag-drop__header">
			<h3 class="tour-schedule-drag-drop__title">{{ tour.name || 'Tour - Title' }}</h3>
			<button 
				class="tour-schedule-drag-drop__add-btn"
				@click="showAvailableComps = !showAvailableComps"
			>
				<theme-icon icon="plus" type="icons" />
				Add Comp
			</button>
		</div>

		<!-- Schedule Configuration -->
		<div class="tour-schedule-drag-drop__config">
			<div class="tour-schedule-drag-drop__config-group">
				<label class="tour-schedule-drag-drop__config-label">Tour Date</label>
				<input 
					type="date" 
					v-model="tourDate"
					class="tour-schedule-drag-drop__config-input"
				/>
			</div>

			<div class="tour-schedule-drag-drop__config-group">
				<label class="tour-schedule-drag-drop__config-label">Start Time</label>
				<input 
					type="time" 
					v-model="configStartTime"
					class="tour-schedule-drag-drop__config-input"
				/>
			</div>

			<div class="tour-schedule-drag-drop__config-group">
				<label class="tour-schedule-drag-drop__config-label">End Time</label>
				<input 
					type="time" 
					v-model="configEndTime"
					class="tour-schedule-drag-drop__config-input"
				/>
			</div>

			<div class="tour-schedule-drag-drop__config-group">
				<themed-button
					variant="default"
					size="sm"
					@click="showAddEventModal = true"
				>
					<theme-icon icon="plus" type="icons" />
					Add Event
				</themed-button>
			</div>
		</div>

		<!-- Available Comps Drawer -->
		<transition name="slide-down">
			<div v-if="showAvailableComps" class="tour-schedule-drag-drop__available">
				<h4 class="tour-schedule-drag-drop__available-title">Available Comps</h4>
				<div class="tour-schedule-drag-drop__available-list">
					<div class="tour-schedule-drag-drop__available-grid">
						<div
							v-for="comp in availableCompsToAdd"
							:key="comp.pivot.id"
							class="tour-schedule-drag-drop__available-item"
							@click="quickAddComp(comp)"
						>
							<theme-icon icon="building" type="icons" />
							<span class="tour-schedule-drag-drop__available-name">
								{{ getCompName(comp) }}
							</span>
							<span class="tour-schedule-drag-drop__available-address">
								{{ getCompAddress(comp) }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</transition>

		<!-- Two Column Layout: Schedule + Map -->
		<div class="tour-schedule-drag-drop__content">
			<!-- Left Column: Calendar View -->
			<div class="tour-schedule-drag-drop__calendar">
				<div class="tour-schedule-drag-drop__calendar-grid">
					<!-- Time labels (left column) -->
					<div class="tour-schedule-drag-drop__time-column">
						<div
							v-for="hour in hourSlots"
							:key="hour"
							class="tour-schedule-drag-drop__time-label"
						>
							{{ formatHour(hour) }}
						</div>
					</div>

					<!-- Events area (right column) -->
					<div class="tour-schedule-drag-drop__events-column">
						<!-- Hour grid lines -->
						<div
							v-for="hour in hourSlots"
							:key="`grid-${hour}`"
							class="tour-schedule-drag-drop__hour-grid"
						></div>

						<!-- Events overlaid on grid -->
						<div
							v-for="(event, index) in scheduleEvents"
							:key="event.id"
							class="tour-schedule-drag-drop__event"
							:class="`tour-schedule-drag-drop__event--${event.type}`"
							:style="getEventStyle(event)"
							@click="editEvent(index)"
						>
							<div class="tour-schedule-drag-drop__event-content">
								<div class="tour-schedule-drag-drop__event-header">
									<theme-icon 
										:icon="getEventIcon(event.type)" 
										type="icons" 
									/>
									<span class="tour-schedule-drag-drop__event-time">
										{{ formatEventTime(event) }}
									</span>
									<button
										class="tour-schedule-drag-drop__event-remove"
										@click.stop="removeEvent(index)"
									>
										<theme-icon icon="close" type="icons" />
									</button>
								</div>
								<div class="tour-schedule-drag-drop__event-title">
									{{ getEventTitle(event) }}
								</div>
								<div v-if="event.type === 'comp'" class="tour-schedule-drag-drop__event-subtitle">
									{{ getCompAddress(event.comp) }}
								</div>
							</div>
						</div>

						<!-- Click to add event -->
						<div 
							class="tour-schedule-drag-drop__click-overlay"
							@click="handleGridClick"
						></div>
					</div>
				</div>
			</div>

			<!-- Right Column: Map -->
			<div class="tour-schedule-drag-drop__map">
				<div v-if="!hasFilledSlots" class="tour-schedule-drag-drop__map-empty">
					<theme-icon icon="map" type="icons" />
					<p>Add comps to see them on the map</p>
				</div>
				<div v-else ref="mapContainer" class="tour-schedule-drag-drop__map-container"></div>
			</div>
		</div>

		<!-- Actions -->
		<div class="tour-schedule-drag-drop__actions">
			<div class="tour-schedule-drag-drop__actions-right">
				<themed-button
					variant="secondary"
					@click="optimizeRoute"
					:disabled="!hasFilledSlots || optimizing"
				>
					<theme-icon icon="navigation" type="icons" />
					{{ optimizing ? 'Optimizing...' : 'Optimize Route' }}
				</themed-button>

				<themed-button
					variant="default"
					@click="saveSchedule"
					:disabled="saving || !hasChanges"
				>
					{{ saving ? 'Saving...' : 'Save Schedule' }}
				</themed-button>
			</div>
		</div>

		<!-- Add/Edit Event Modal -->
		<div v-if="showAddEventModal || showEditEventModal" class="tour-schedule-drag-drop__modal-overlay" @click="closeEventModal">
			<div class="tour-schedule-drag-drop__modal" @click.stop>
				<div class="tour-schedule-drag-drop__modal-header">
					<h3>{{ showEditEventModal ? 'Edit Event' : 'Add Event' }}</h3>
					<button @click="closeEventModal" class="tour-schedule-drag-drop__modal-close">
						<theme-icon icon="close" type="icons" />
					</button>
				</div>
				<div class="tour-schedule-drag-drop__modal-body">
					<!-- Event Type -->
					<div class="tour-schedule-drag-drop__modal-field">
						<label>Event Type:</label>
						<select v-model="eventForm.type" class="tour-schedule-drag-drop__config-input">
							<option value="comp">Comp Visit</option>
							<option value="lunch">Lunch Break</option>
							<option value="drive">Drive Time</option>
						</select>
					</div>

					<!-- Comp Selection (only for comp type) -->
					<div v-if="eventForm.type === 'comp'" class="tour-schedule-drag-drop__modal-field">
						<label>Select Comp:</label>
						<select v-model="eventForm.compId" class="tour-schedule-drag-drop__config-input">
							<option :value="null">-- Select a comp --</option>
							<option 
								v-for="comp in availableCompsToAdd" 
								:key="comp.pivot?.id || comp.id"
								:value="comp.pivot?.id || comp.id"
							>
								{{ getCompName(comp) }} - {{ getCompAddress(comp) }}
							</option>
						</select>
					</div>

					<!-- Start Time -->
					<div class="tour-schedule-drag-drop__modal-field">
						<label>Start Time:</label>
						<input 
							type="time" 
							v-model="eventForm.startTime"
							class="tour-schedule-drag-drop__config-input"
						/>
					</div>

					<!-- Duration -->
					<div class="tour-schedule-drag-drop__modal-field">
						<label>Duration:</label>
						<select 
							v-if="eventForm.type === 'drive'"
							v-model.number="eventForm.duration" 
							class="tour-schedule-drag-drop__config-input"
						>
							<option v-for="min in driveTimeOptions" :key="min" :value="min">{{ min }} minutes</option>
						</select>
						<select 
							v-else
							v-model.number="eventForm.duration" 
							class="tour-schedule-drag-drop__config-input"
						>
							<option :value="30">30 minutes</option>
							<option :value="45">45 minutes</option>
							<option :value="60">60 minutes</option>
						</select>
					</div>

					<div class="tour-schedule-drag-drop__modal-actions">
						<themed-button variant="secondary" @click="closeEventModal">Cancel</themed-button>
						<themed-button variant="default" @click="saveEvent">
							{{ showEditEventModal ? 'Update' : 'Add' }}
						</themed-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
	name: 'TourScheduleDragDrop',

	components: {
		ThemeIcon,
		ThemedButton
	},

	props: {
		tour: {
			type: Object,
			required: true
		},
		availableComps: {
			type: Array,
			default: () => []
		},
		startTime: {
			type: String,
			default: '08:00'
		},
		slotDuration: {
			type: Number,
			default: 60 // minutes
		},
		initialSlotCount: {
			type: Number,
			default: 12 // 8 AM to 8 PM (12 hours for a full tour day)
		}
	},

	data() {
		return {
			scheduleEvents: [], // Array of events with startTime, duration, type, comp
			showAvailableComps: false,
			saving: false,
			optimizing: false,
			hasChanges: false,
			existingTourComps: [],
			map: null,
			markers: [],
			polyline: null,
			configStartTime: '08:00',
			configEndTime: '18:00',
			tourDate: null,
			showAddEventModal: false,
			showEditEventModal: false,
			editingEventIndex: null,
			eventForm: {
				type: 'comp',
				compId: null,
				startTime: '09:00',
				duration: 60
			}
		};
	},

	computed: {
		hourSlots() {
			// Generate hour slots from start to end time
			const [startHour] = this.configStartTime.split(':').map(Number);
			const [endHour] = this.configEndTime.split(':').map(Number);
			const hours = [];
			for (let h = startHour; h <= endHour; h++) {
				hours.push(h);
			}
			return hours;
		},

		availableCompsToAdd() {
			// Filter out comps that are already added to the schedule
			const addedCompIds = this.scheduleEvents
				.filter(event => event.type === 'comp' && event.comp)
				.map(event => event.comp.pivot?.id || event.comp.id);
			
			return this.availableComps.filter(comp => {
				const compId = comp.pivot?.id || comp.id;
				return !addedCompIds.includes(compId);
			});
		},

		hasFilledSlots() {
			return this.scheduleEvents.some(event => event.type === 'comp');
		},

		filledComps() {
			return this.scheduleEvents
				.filter(event => event.type === 'comp' && event.comp)
				.map(event => event.comp);
		},

		driveTimeOptions() {
			const options = [];
			for (let i = 10; i <= 60; i += 5) {
				options.push(i);
			}
			return options;
		}
	},

	mounted() {
		this.loadExistingSchedule();
	},

	watch: {
		filledComps: {
			handler() {
				this.$nextTick(() => {
					this.updateMap();
				});
			},
			deep: true
		}
	},

	methods: {
		async initializeMap() {
			if (!this.$refs.mapContainer || !window.google) return;

			// Default center (will be adjusted to fit markers)
			this.map = new google.maps.Map(this.$refs.mapContainer, {
				zoom: 12,
				center: { lat: 40.7128, lng: -74.0060 }, // Default to NYC
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: false
			});

			this.updateMapMarkers();
		},

		updateMap() {
			if (!this.hasFilledSlots) {
				// Clear map if no comps
				if (this.map) {
					this.clearMapMarkers();
				}
				return;
			}

			if (!this.map) {
				this.initializeMap();
			} else {
				this.updateMapMarkers();
			}
		},

		updateMapMarkers() {
			if (!this.map) return;

			// Clear existing markers and polyline
			this.clearMapMarkers();

			const bounds = new google.maps.LatLngBounds();
			const path = [];

			// Add markers for each filled comp
			this.filledComps.forEach((comp, index) => {
				const lat = this.getCompLat(comp);
				const lng = this.getCompLng(comp);

				if (!lat || !lng) return;

				const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
				bounds.extend(position);
				path.push(position);

				const marker = new google.maps.Marker({
					position,
					map: this.map,
					label: {
						text: `${index + 1}`,
						color: 'white',
						fontSize: '14px',
						fontWeight: 'bold'
					},
					title: this.getCompName(comp)
				});

				this.markers.push(marker);
			});

			// Draw route line if there are multiple comps
			if (path.length > 1) {
				this.polyline = new google.maps.Polyline({
					path,
					geodesic: true,
					strokeColor: '#4F46E5',
					strokeOpacity: 0.8,
					strokeWeight: 3,
					map: this.map
				});
			}

			// Fit map to show all markers
			if (this.markers.length > 0) {
				this.map.fitBounds(bounds);
			}
		},

		clearMapMarkers() {
			// Remove all markers
			this.markers.forEach(marker => marker.setMap(null));
			this.markers = [];

			// Remove polyline
			if (this.polyline) {
				this.polyline.setMap(null);
				this.polyline = null;
			}
		},

		getCompLat(comp) {
			if (comp.latitude) return comp.latitude;
			if (comp.building?.latitude) return comp.building.latitude;
			return null;
		},

		getCompLng(comp) {
			if (comp.longitude) return comp.longitude;
			if (comp.building?.longitude) return comp.building.longitude;
			return null;
		},
		async loadExistingSchedule() {
			try {
				const response = await axios.get(`/tours/${this.tour.id}`);
				this.existingTourComps = response.data.tour_comps || [];

				// Initialize time slots
				this.initializeTimeSlots();

				// Fill slots with existing comps
				this.existingTourComps
					.filter(tc => tc.slot_type === 'comp')
					.sort((a, b) => a.sequence_order - b.sequence_order)
					.forEach((tc, index) => {
						if (index < this.timeSlots.length) {
							// Find the comp in availableComps
							const comp = this.availableComps.find(c => c.pivot?.id === tc.comp_id);
							if (comp) {
								this.timeSlots[index].comp = comp;
								this.timeSlots[index].tourCompId = tc.id;
							}
						}
					});
			} catch (error) {
				console.error('Error loading schedule:', error);
				this.initializeTimeSlots();
			}
		},

		initializeTimeSlots() {
			const slots = [];
			const [startHours, startMinutes] = this.configStartTime.split(':').map(Number);
			const [endHours, endMinutes] = this.configEndTime.split(':').map(Number);
			
			let currentTime = new Date();
			currentTime.setHours(startHours, startMinutes, 0, 0);
			
			const endTime = new Date();
			endTime.setHours(endHours, endMinutes, 0, 0);

			let slotIndex = 0;

			// Create empty slots - user will manually add comps, lunch, and drive time
			while (currentTime < endTime) {
				slots.push({
					id: `slot-${slotIndex}`,
					time: this.formatTime(currentTime),
					type: null, // 'comp', 'lunch', 'drive', or null
					comp: null,
					duration: null,
					tourCompId: null
				});
				
				// Move to next time slot (use smallest increment for flexibility)
				currentTime.setMinutes(currentTime.getMinutes() + 15);
				slotIndex++;
			}

			this.timeSlots = slots;
		},

		regenerateTimeSlots() {
			// Save current slots
			const currentSlots = this.timeSlots.filter(slot => slot.type);

			// Regenerate time slots
			this.initializeTimeSlots();

			// Try to restore slots (this is basic - may need adjustment)
			let newIndex = 0;
			for (const oldSlot of currentSlots) {
				if (newIndex < this.timeSlots.length) {
					this.timeSlots[newIndex] = {
						...this.timeSlots[newIndex],
						type: oldSlot.type,
						comp: oldSlot.comp,
						duration: oldSlot.duration,
						tourCompId: oldSlot.tourCompId
					};
					newIndex++;
				}
			}

			this.hasChanges = true;
		},

		formatTime(date) {
			const hours = date.getHours();
			const minutes = date.getMinutes();
			const ampm = hours >= 12 ? 'PM' : 'AM';
			const displayHours = hours % 12 || 12;
			const displayMinutes = minutes.toString().padStart(2, '0');
			return `${displayHours}:${displayMinutes} ${ampm}`;
		},

		cloneComp(comp) {
			return { ...comp };
		},

		getSlotList(index) {
			// Return an array with the comp if it exists, or empty array
			const slot = this.timeSlots[index];
			if (slot.type === 'comp' && slot.comp) {
				return [slot.comp];
			}
			return [];
		},

		handleSlotChange(evt, index) {
			this.hasChanges = true;

			// Handle adding comp to slot
			if (evt.added) {
				this.timeSlots[index].type = 'comp';
				this.timeSlots[index].comp = evt.added.element;
				this.timeSlots[index].duration = this.configSlotDuration;
			}

			// Handle removing comp from slot
			if (evt.removed) {
				this.timeSlots[index].type = null;
				this.timeSlots[index].comp = null;
				this.timeSlots[index].duration = null;
			}
		},

		addSlot(index, type) {
			if (type === 'comp') {
				this.selectedSlotIndex = index;
				this.selectedDuration = this.configSlotDuration;
				this.showCompSelectorModal = true;
			} else if (type === 'lunch') {
				this.timeSlots[index].type = 'lunch';
				this.timeSlots[index].duration = 60; // Default 60 min
				this.hasChanges = true;
			} else if (type === 'drive') {
				this.timeSlots[index].type = 'drive';
				this.timeSlots[index].duration = this.defaultDriveTime;
				this.hasChanges = true;
			}
		},

		removeSlot(index) {
			this.timeSlots[index].type = null;
			this.timeSlots[index].comp = null;
			this.timeSlots[index].duration = null;
			this.timeSlots[index].tourCompId = null;
			this.hasChanges = true;
		},

		editSlot(index) {
			this.editingSlotIndex = index;
			this.editingSlot = this.timeSlots[index];
			this.editDuration = this.editingSlot.duration || 60;
			this.showDurationModal = true;
		},

		saveDuration() {
			if (this.editingSlotIndex !== null) {
				this.timeSlots[this.editingSlotIndex].duration = this.editDuration;
				this.hasChanges = true;
			}
			this.closeDurationModal();
		},

		closeDurationModal() {
			this.showDurationModal = false;
			this.editingSlotIndex = null;
			this.editingSlot = null;
			this.editDuration = 60;
		},

		selectComp(comp) {
			if (this.selectedSlotIndex !== null) {
				this.timeSlots[this.selectedSlotIndex].type = 'comp';
				this.timeSlots[this.selectedSlotIndex].comp = comp;
				this.timeSlots[this.selectedSlotIndex].duration = this.selectedDuration;
				this.hasChanges = true;
			}
			this.closeCompSelector();
		},

		closeCompSelector() {
			this.showCompSelectorModal = false;
			this.selectedSlotIndex = null;
			this.selectedDuration = 60;
		},

		async saveSchedule() {
			this.saving = true;
			try {
				// Get filled slots with comps
				const filledSlots = this.timeSlots
					.map((slot, index) => ({
						slot,
						index,
						comp: slot.comp
					}))
					.filter(item => item.comp);

				// Prepare comp IDs in order
				const compIds = filledSlots.map(item => item.comp.pivot.id);

				// First, update the tour comps order
				await axios.put(`/tours/${this.tour.id}/comps/reorder`, {
					comp_ids: compIds
				});

				// Then generate schedule with times
				const startTimeSlot = filledSlots[0];
				if (startTimeSlot) {
					await axios.post(`/tours/${this.tour.id}/schedule/generate`, {
						start_time: this.convertTo24Hour(startTimeSlot.slot.time),
						comp_duration: this.slotDuration,
						lunch_duration: 60,
						lunch_after_comps: 3
					});
				}

				this.hasChanges = false;
				this.$emit('schedule-saved');
				this.$notify({
					type: 'success',
					message: 'Schedule saved successfully!'
				});

				// Reload to get updated data
				await this.loadExistingSchedule();

			} catch (error) {
				console.error('Error saving schedule:', error);
				this.$notify({
					type: 'error',
					message: error.response?.data?.message || 'Failed to save schedule'
				});
			} finally {
				this.saving = false;
			}
		},

		async optimizeRoute() {
			if (!confirm('This will reorder your comps for the most efficient route. Continue?')) {
				return;
			}

			this.optimizing = true;
			try {
				const response = await axios.post(`/tours/${this.tour.id}/routing/optimize`);
				
				if (response.data.success) {
					this.$notify({
						type: 'success',
						message: 'Route optimized successfully!'
					});
					await this.loadExistingSchedule();
				}
			} catch (error) {
				this.$notify({
					type: 'error',
					message: error.response?.data?.message || 'Failed to optimize route'
				});
			} finally {
				this.optimizing = false;
			}
		},

		convertTo24Hour(time12h) {
			const [time, modifier] = time12h.split(' ');
			let [hours, minutes] = time.split(':');
			
			if (hours === '12') {
				hours = '00';
			}
			
			if (modifier === 'PM') {
				hours = parseInt(hours, 10) + 12;
			}
			
			return `${hours.toString().padStart(2, '0')}:${minutes}`;
		},

		getCompName(comp) {
			if (!comp) return '';
			
			// Try different possible paths for building name
			if (comp.building_name) return comp.building_name;
			if (comp.name) return comp.name;
			if (comp.address_line_1) return comp.address_line_1;
			
			// Try nested building object
			if (comp.building) {
				if (comp.building.building_name) return comp.building.building_name;
				if (comp.building.name) return comp.building.name;
				if (comp.building.address_line_1) return comp.building.address_line_1;
			}
			
			return 'Unnamed Building';
		},

		getCompAddress(comp) {
			if (!comp) return '';
			
			const parts = [];
			
			// Try direct properties first
			if (comp.city) parts.push(comp.city);
			if (comp.state) parts.push(comp.state);
			
			// Try nested building object
			if (parts.length === 0 && comp.building) {
				if (comp.building.city) parts.push(comp.building.city);
				if (comp.building.state) parts.push(comp.building.state);
			}
			
			return parts.join(', ');
		}
	}
};
</script>
